import { el } from './dom';

type PreviewItem = { id: string; value: string; label: string; on: boolean };

const PII_ITEMS: PreviewItem[] = [
  { id: 'sean-carter', value: 'Sean Carter', label: 'name', on: false },
  { id: 'email', value: 'scarter@example.com', label: 'email', on: false },
  { id: 'maya-lin', value: 'Maya Lin', label: 'name', on: false },
  { id: 'date', value: 'March 4th, 2026', label: 'date', on: false },
  { id: 'address', value: '412 Oak Street...', label: 'address', on: true },
  { id: 'phone', value: '(301) 555-0142', label: 'phone', on: true },
];

const SIDE_TOTAL_FOUND = 12;

const sidebarHtml = (rows: readonly PreviewItem[], reviewing: number): string => {
  const items = rows
    .map(
      (r) => `
        <div class="preview-item">
          <div class="preview-item-info">
            <div class="preview-item-value">${r.value}</div>
            <div class="preview-item-label">${r.label}</div>
          </div>
          <div class="preview-checkbox${r.on ? ' checked' : ''}" data-pii-id="${r.id}"></div>
        </div>`,
    )
    .join('');
  return `
    <div class="preview-sidebar-header" data-side-label>${String(SIDE_TOTAL_FOUND)} found · <span class="accent">${String(reviewing)} reviewing</span></div>
    ${items}
    <button type="button" class="preview-cta">redact &amp; download →</button>`;
};

/**
 * Marketing demo of the in-app review panel. Document on the left,
 * checkbox sidebar on the right. Clicking a checkbox toggles the
 * matching span between pending (dashed underline) and committed
 * (black bar). Pure visual — no shared state with the real review
 * pipeline.
 */
export const PreviewFrame = (): HTMLElement => {
  const initialReviewing = PII_ITEMS.filter((r) => !r.on).length;

  const root = el(`
    <section class="preview-section">
      <div class="container">
        <div class="section-header">
          <span class="label">the part you actually use</span>
          <h2>review the redactions before you commit<span class="accent-period">.</span></h2>
        </div>

        <div class="preview-frame">
          <div class="preview-doc">
            <div class="preview-doc-header">apartment_lease_2026.pdf — page 1 of 14</div>
            <h3>Residential Lease Agreement</h3>
            <p>This Lease Agreement ("Agreement") is entered into on
              <span class="pii-pending" data-pii-id="date">March 4th, 2026</span>, by and between
              <span class="pii-pending" data-pii-id="sean-carter">Sean Carter</span> ("Landlord") and
              <span class="pii-pending" data-pii-id="maya-lin">Maya Lin</span> ("Tenant").
            </p>
            <p>Tenant's current address is
              <span class="pii-pending" data-pii-id="address">412 Oak Street, Apt 3B, Bethesda, MD 20814</span>. Tenant's
              contact phone number is <span class="pii-pending" data-pii-id="phone">(301) 555-0142</span> and email
              <span class="pii-pending" data-pii-id="email">scarter@example.com</span>.
            </p>
            <p>The Premises shall be occupied solely by the Tenant and Tenant's spouse,
              <span class="pii-pending" data-pii-id="maya-lin">Maya Lin</span>, and no other person. Tenant agrees to
              provide a security deposit of $1,400 prior to occupancy.
            </p>
            <p>For verification, Tenant has provided a valid driver's license issued by the State
              of Maryland, number <span class="pii-static-bar"></span>, with expiration
              date <span class="pii-static-bar pii-bar-short"></span>.
            </p>
          </div>
          <div class="preview-sidebar">${sidebarHtml(PII_ITEMS, initialReviewing)}</div>
        </div>
      </div>
    </section>
  `);

  const syncOne = (id: string, on: boolean): void => {
    root.querySelectorAll<HTMLElement>(`.preview-doc [data-pii-id="${id}"]`).forEach((span) => {
      span.classList.toggle('pii-pending', !on);
      span.classList.toggle('pii-committed', on);
    });
  };
  for (const item of PII_ITEMS) syncOne(item.id, item.on);

  root.querySelectorAll<HTMLElement>('.preview-checkbox').forEach((box) => {
    box.addEventListener('click', () => {
      const id = box.dataset.piiId;
      if (!id) return;
      const turningOn = !box.classList.contains('checked');
      box.classList.toggle('checked');
      syncOne(id, turningOn);
      const reviewing = root.querySelectorAll('.preview-checkbox:not(.checked)').length;
      const sideLabel = root.querySelector<HTMLElement>('[data-side-label]');
      if (sideLabel) {
        sideLabel.innerHTML = `${String(SIDE_TOTAL_FOUND)} found · <span class="accent">${String(reviewing)} reviewing</span>`;
      }
    });
  });

  return root;
};
